import * as pdfjsLib from "pdfjs-dist";
import type { PDFDocumentProxy, PDFPageProxy } from "pdfjs-dist";
import { ensurePromiseWithResolvers } from "../../polyfills";

// Import worker URL using Vite's ?url suffix for proper bundling
// This ensures the worker file is included in the build and gets a correct URL
import pdfjsWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

// Ensure Promise.withResolvers is available in the main thread
// The worker (ES module) handles its own polyfills internally
ensurePromiseWithResolvers();

// Set worker source directly - pdfjs-dist v5 uses ES module workers
// which handle their own scope and don't need wrapping
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

// Maximum dimension for rendered PDF to prevent memory issues with large files
const MAX_DIMENSION = 2400;

/**
 * Calculate optimal scale for PDF rendering to stay within memory limits
 * @param page - PDF page to measure
 * @param targetScale - Desired scale factor
 * @returns Adjusted scale that keeps dimensions within MAX_DIMENSION
 */
const calculateSafeScale = (
  pageWidth: number,
  pageHeight: number,
  targetScale: number,
): number => {
  const scaledWidth = pageWidth * targetScale;
  const scaledHeight = pageHeight * targetScale;
  const maxDimension = Math.max(scaledWidth, scaledHeight);

  if (maxDimension <= MAX_DIMENSION) {
    return targetScale;
  }

  // Scale down to fit within MAX_DIMENSION
  return MAX_DIMENSION / Math.max(pageWidth, pageHeight);
};

/**
 * Extracts a PDF page as an image
 * Includes proper cleanup of PDF.js resources to prevent memory leaks
 * Uses dynamic scaling to prevent memory issues with large PDFs
 * @param file - PDF File object
 * @param targetScale - Desired render scale (default 2 for high quality, auto-adjusted for large PDFs)
 * @param pageNumber - 1-based page number to render
 * @returns Promise resolving to a data URL of the first page as PNG
 */
export const pdfToImage = async (
  file: File,
  targetScale: number = 2,
  pageNumber: number = 1,
): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();

  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  let pdf: PDFDocumentProxy | null = null;
  let page: PDFPageProxy | null = null;

  try {
    // Load the PDF document
    pdf = await loadingTask.promise;

    if (pageNumber < 1 || pageNumber > pdf.numPages) {
      throw new Error(`Página ${pageNumber} fuera de rango para este PDF`);
    }

    page = await pdf.getPage(pageNumber);

    // Get base viewport to measure page dimensions
    const baseViewport = page.getViewport({ scale: 1 });

    // Calculate safe scale to prevent memory issues
    const safeScale = calculateSafeScale(
      baseViewport.width,
      baseViewport.height,
      targetScale,
    );

    // Get the viewport at the safe scale
    const viewport = page.getViewport({ scale: safeScale });

    // Create a canvas to render the page
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("No se pudo crear el contexto del canvas");
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    // Render the page to the canvas
    await page.render({
      canvasContext: context,
      viewport: viewport,
      canvas: canvas,
    }).promise;

    // Convert canvas to data URL
    const dataUrl = canvas.toDataURL("image/png");

    // Clean up canvas to free memory
    canvas.width = 0;
    canvas.height = 0;

    return dataUrl;
  } finally {
    // Clean up PDF.js resources to prevent memory leaks
    if (page) {
      page.cleanup();
    }
    // In pdfjs-dist 6, destroying the loading task also destroys the document
    await loadingTask.destroy();
  }
};

/**
 * Gets the number of pages in a PDF file.
 */
export const getPdfPageCount = async (file: File): Promise<number> => {
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  let pdf: PDFDocumentProxy | null = null;

  try {
    pdf = await loadingTask.promise;
    return pdf.numPages;
  } finally {
    // In pdfjs-dist 6, destroying the loading task also destroys the document
    await loadingTask.destroy();
  }
};

const dataUrlToFile = async (
  dataUrl: string,
  filename: string,
  type: string,
): Promise<File> => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type });
};

/**
 * Renders a PDF page and returns it as an image File that can be sent to OCR.
 */
export const pdfPageToImageFile = async (
  file: File,
  pageNumber: number,
  targetScale: number = 2,
): Promise<File> => {
  const imageDataUrl = await pdfToImage(file, targetScale, pageNumber);
  const baseName = file.name.replace(/\.[^/.]+$/, "") || "document";
  return dataUrlToFile(
    imageDataUrl,
    `${baseName}_page_${pageNumber}.png`,
    "image/png",
  );
};

/**
 * Checks if a file is a PDF
 * @param file - File to check
 * @returns true if the file is a PDF
 */
export const isPdfFile = (file: File): boolean => {
  return (
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  );
};
